/**
 * SafeAudioEngine — a click-free, speaker-safe Web Audio synthesizer.
 *
 * SAFETY DESIGN (informed by a real speaker-damage incident):
 *
 * 1. **No instant gain jumps.** Every note uses setValueAtTime(0) at the
 *    oscillator start, then linearRampToValueAtTime over a short attack.
 *    This eliminates the transient "crack" that occurs when an oscillator
 *    is started at full amplitude (a step discontinuity = broadband click).
 *
 * 2. **Soft release.** On note-off the gain ramps to 0 over ~60 ms before
 *    the oscillator is stopped, preventing a click at note end.
 *
 * 3. **Global low-pass filter + compressor.** A DynamicsCompressor with a
 *    low threshold acts as a brick-wall limiter. A BiquadFilter at 8 kHz
 *    rolls off harsh high-frequency content that can damage small tweeters.
 *
 * 4. **Hard volume cap.** Master gain is clamped to 0.35 (≈ -9 dBFS) even
 *    if the user sets the slider to 100%. No combination of polyphony can
 *    produce a signal louder than the limiter allows.
 *
 * 5. **Polyphony cap.** Maximum 16 simultaneous voices to prevent summing
 *    overload. Oldest voice is stolen with a quick ramp-down.
 *
 * 6. **Sine-only default waveform.** Sine has no harmonics and is the
 *    gentlest possible waveform for speakers. Triangle is available as an
 *    option but still filtered.
 *
 * 7. **AudioContext resume on first gesture.** Browsers suspend
 *    AudioContext until a user gesture. We resume lazily on first noteOn
 *    to avoid cold-start artefacts.
 */

type WaveformType = 'sine' | 'triangle' | 'square' | 'sawtooth';

interface Voice {
  oscillator: OscillatorNode;
  gainNode: GainNode;
  keyId: string;
  startTime: number;
  releaseTimer: ReturnType<typeof setTimeout> | null;
}

const MAX_VOICES = 16;
const MAX_MASTER_GAIN = 0.35;
const ATTACK_TIME = 0.008; // 8 ms — short enough to feel responsive, long enough to avoid click
const RELEASE_TIME = 0.06; // 60 ms
const FILTER_FREQ = 8000; // Hz low-pass

export class SafeAudioEngine {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private compressor: DynamicsCompressorNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private voices: Map<string, Voice> = new Map();
  private voiceOrder: string[] = []; // for stealing oldest

  private volume = 0.5; // 0..1 slider value (mapped to 0..MAX_MASTER_GAIN)
  private waveform: WaveformType = 'sine';

  /** Lazily create / resume the AudioContext. Must be called from a user gesture. */
  private ensureContext(): AudioContext {
    if (!this.ctx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctor();

      // Build the master chain: voices → filter → compressor → masterGain → destination
      this.filter = this.ctx.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = FILTER_FREQ;
      this.filter.Q.value = 0.7;

      this.compressor = this.ctx.createDynamicsCompressor();
      this.compressor.threshold.value = -10;
      this.compressor.knee.value = 6;
      this.compressor.ratio.value = 12;
      this.compressor.attack.value = 0.003;
      this.compressor.release.value = 0.1;

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0; // start at zero, ramp to target
      this.masterGain.gain.setValueAtTime(0, this.ctx.currentTime);
      this.masterGain.gain.linearRampToValueAtTime(this.volume * MAX_MASTER_GAIN, this.ctx.currentTime + 0.05);

      this.filter.connect(this.compressor);
      this.compressor.connect(this.masterGain);
      this.masterGain.connect(this.ctx.destination);
    }

    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }

    return this.ctx;
  }

  /** Set master volume (0..1). Clamped and scaled to safe max. */
  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.ctx && this.masterGain) {
      const t = this.ctx.currentTime;
      this.masterGain.gain.cancelScheduledValues(t);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, t);
      this.masterGain.gain.linearRampToValueAtTime(this.volume * MAX_MASTER_GAIN, t + 0.03);
    }
  }

  setWaveform(w: WaveformType): void {
    this.waveform = w;
  }

  /**
   * Start a note. `keyId` identifies the key for later noteOff.
   * `frequency` is the exact microtonal frequency in Hz.
   * `velocity` is 0..1.
   */
  noteOn(keyId: string, frequency: number, velocity = 0.7): void {
    const ctx = this.ensureContext();
    const now = ctx.currentTime;

    // If this key is already sounding, stop it first (prevents stacking)
    if (this.voices.has(keyId)) {
      this.noteOff(keyId, true);
    }

    // Polyphony cap: steal oldest voice
    if (this.voices.size >= MAX_VOICES) {
      const oldest = this.voiceOrder[0];
      if (oldest) this.noteOff(oldest, true);
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.type = this.waveform;
    oscillator.frequency.value = Math.max(20, Math.min(20000, frequency));

    // CRITICAL: start gain at zero to prevent click
    gainNode.gain.setValueAtTime(0, now);
    // Short linear ramp for attack — smooth onset, no transient
    gainNode.gain.linearRampToValueAtTime(velocity * 0.5, now + ATTACK_TIME);

    oscillator.connect(gainNode);
    gainNode.connect(this.filter!);

    oscillator.start(now);

    const voice: Voice = { oscillator, gainNode, keyId, startTime: now, releaseTimer: null };
    this.voices.set(keyId, voice);
    this.voiceOrder.push(keyId);
  }

  /** Stop a note. If `fast` is true, use a very short release (for voice stealing). */
  noteOff(keyId: string, fast = false): void {
    const voice = this.voices.get(keyId);
    if (!voice) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;
    const releaseDuration = fast ? 0.005 : RELEASE_TIME;

    // Capture current gain value and ramp to zero
    const currentGain = voice.gainNode.gain.value;
    voice.gainNode.gain.cancelScheduledValues(now);
    voice.gainNode.gain.setValueAtTime(currentGain, now);
    voice.gainNode.gain.linearRampToValueAtTime(0, now + releaseDuration);

    // Stop oscillator after release completes
    voice.oscillator.stop(now + releaseDuration + 0.01);

    voice.oscillator.onended = () => {
      try {
        voice.oscillator.disconnect();
        voice.gainNode.disconnect();
      } catch {
        // already disconnected
      }
    };

    this.voices.delete(keyId);
    this.voiceOrder = this.voiceOrder.filter((k) => k !== keyId);

    if (voice.releaseTimer) clearTimeout(voice.releaseTimer);
  }

  /** Stop all currently sounding notes immediately. */
  allNotesOff(): void {
    for (const keyId of [...this.voices.keys()]) {
      this.noteOff(keyId, true);
    }
  }

  /** Get the number of currently active voices. */
  get activeVoiceCount(): number {
    return this.voices.size;
  }

  /** Is the audio context running? */
  get isRunning(): boolean {
    return this.ctx?.state === 'running';
  }
}

/** Singleton instance — one engine for the whole app. */
let engineInstance: SafeAudioEngine | null = null;

export function getAudioEngine(): SafeAudioEngine {
  if (!engineInstance) {
    engineInstance = new SafeAudioEngine();
  }
  return engineInstance;
}
